<template>
  <div
    v-if="request"
    class="sr-detail px-4"
  >
    <b-row class="d-flex align-items-center mb-2">
      <b-col class="d-flex align-items-center">
        <span
          qa="request-label"
          class="ap-title ap-title__main mr-2 mb-0"
        >
          <i
            class="far fa-file-medical mr-2"
            aria-hidden="true"
          />{{ request.label }}
        </span>
        <b-badge
          v-if="request.automaticallyCreated"
          v-tooltip="$t('global.automaticCreation')"
          qa="automatically-created-badge"
          pill
          class="ml-2 icon-badge indicator-bubble d-flex align-items-center justify-content-center"
        >
          <i
            class="far fa-magic"
            aria-hidden="true"
          />
        </b-badge>
        <study-request-priority-dot
          qa="priority"
          class="ml-2"
          :priority="request.priority"
          :size="10"
        />
        <study-request-status-badge
          qa="status-badge"
          class="status-badge ml-2"
          :status="request.status"
        />
      </b-col>
      <b-col
        cols="auto"
        class="d-flex justify-content-end"
      >
        <div class="sr-detail-head-buttons">
          <slot name="buttons-end" />
        </div>
      </b-col>
    </b-row>
    <b-row class="mt-3">
      <b-col>
        <b-tabs
          ref="tabs"
          pills
          card
          vertical
          class="vt-tabs--vertical"
          @input="$emit('tabIndex', $event)"
        >
          <b-tab lazy>
            <template slot="title">
              <i
                qa="request-information-tab"
                class="fas fa-info"
                aria-hidden="true"
              />
            </template>
            <div class="ml-2">
              <b-row class="content-scroll request-info">
                <b-col class="mt-2">
                  <request-info :request="request" />
                </b-col>
              </b-row>
            </div>
          </b-tab>
          <b-tab lazy>
            <template slot="title">
              <i
                qa="patient-information-tab"
                class="far fa-user-circle"
                aria-hidden="true"
              />
            </template>
            <div class="ml-2">
              <b-row class="d-flex align-items-center py-2 mt-1">
                <b-col>
                  <span class="ap-title ap-title__main">{{ $t('global.infoPatient') }}</span>
                </b-col>
              </b-row>
              <b-row class="content-scroll patient-info">
                <b-col class="mt-2">
                  <patient-info :patient="request.patient" />
                </b-col>
              </b-row>
            </div>
          </b-tab>
          <b-tab lazy>
            <template slot="title">
              <i
                qa="tracking-information-tab"
                class="far fa-map-marker-alt"
                aria-hidden="true"
              />
            </template>
            <div class="ml-2">
              <b-row class="d-flex align-items-center py-2 mt-1">
                <b-col>
                  <span class="ap-title ap-title__main">{{ $t('globalLocator.sections.tracking') }}</span>
                </b-col>
              </b-row>
              <b-row class="content-scroll tracking-info">
                <b-col class="mt-2">
                  <tracking-info
                    :tracking-resource="trackingResource"
                    :laboratory-id="request.laboratory.labId"
                    :request-id="request.id"
                  />
                </b-col>
              </b-row>
            </div>
          </b-tab>
          <b-tab
            v-if="visibleCaseReport"
            lazy
          >
            <template slot="title">
              <i
                qa="pathological-report-tab"
                class="far fa-file-contract"
                aria-hidden="true"
              />
            </template>
            <div class="ml-2">
              <b-row class="d-flex align-items-center mt-3">
                <b-col>
                  <span class="ap-title ap-title__main">{{ $t('global.pathologicalReport') }}</span>
                </b-col>
              </b-row>
              <b-row class="content-scroll report-info">
                <b-col class="mt-2">
                  <report-info
                    :cas-id="casId"
                    :lab-id="request.laboratory.labId"
                    @loadSummary="$emit('loadSummary')"
                  />
                </b-col>
              </b-row>
            </div>
          </b-tab>
          <b-tab
            v-if="request.attachmentsCount"
            lazy
          >
            <template slot="title">
              <i
                qa="attachments-tab"
                class="far fa-paperclip"
                aria-hidden="true"
              />
              <div class="section-indicator">
                <vtBubble
                  v-show="request.attachmentsCount"
                  :number="request.attachmentsCount"
                  :type="false"
                  class="mr-1"
                />
              </div>
            </template>
            <div class="ml-2">
              <b-row class="d-flex align-items-center mt-3">
                <b-col>
                  <span class="ap-title ap-title__main">{{ $tc('global.file', request.attachmentsCount) }}</span>
                </b-col>
              </b-row>
              <div class="content-scroll pt-2">
                <request-attachments-info
                  :request="request"
                  @onDeleteAttachment="$emit('onDeleteAttachment')"
                />
              </div>
            </div>
          </b-tab>
          <b-tab
            v-if="request.incidentsCount"
            lazy
          >
            <template slot="title">
              <i
                qa="incidents-tab"
                class="far fa-exclamation-triangle"
                aria-hidden="true"
              />
              <div class="section-indicator">
                <vtBubble
                  v-show="request.incidentsCount"
                  :type="true"
                  :number="request.incidentsCount"
                  class="mr-1"
                />
              </div>
            </template>
            <div class="ml-2">
              <b-row class="d-flex align-items-center mt-3">
                <b-col>
                  <span class="ap-title ap-title__main">{{ $tc('global.incident', request.incidentsCount) }}</span>
                </b-col>
              </b-row>
              <div class="content-scroll pt-2">
                <request-incidents-info
                  :request-id="request.id"
                  :lab-id="request.laboratory.labId"
                />
              </div>
            </div>
          </b-tab>
        </b-tabs>
      </b-col>
    </b-row>
  </div>
</template>

<script>

import clinicalRequestPrintService from '@/modules/printModule/services/ClinicalRequestPrintService'
import StudyRequestPriorityDot from '@/modules/clinicalModule/components/StatusRequestPriorityDot'
import StudyRequestStatusBadge from '@/modules/clinicalModule/components/StudyRequestStatusBadge'
import RequestInfo from '../../../modules/clinicalModule/components/Detail/RequestInfo'
import PatientInfo from '../../searcherPatientModal/PatientInfo'
import ReportInfo from '../../../modules/clinicalModule/components/Detail/ReportInfo'
import RequestAttachmentsInfo from '../../../modules/clinicalModule/components/Detail/RequestAttachmentsInfo'
import RequestIncidentsInfo from '../../../modules/clinicalModule/components/Detail/RequestIncidentsInfo'
import TrackingInfo from '../../../modules/clinicalModule/components/Detail/TrackingInfo'
import sessionStore from '@/session/sessionStore'
import VtBubble from '@/components/common/VtBubble'
import { CaseOperation } from '@/api/operations/models/case-operation'
import { REQUEST_STATES } from '@/api/resources/entityState/request-state'
import commonRequestService from '@/api/resources/common/commonRequestService'

export default {
    name: 'CommonRequestDetail',
    components: {
        StudyRequestPriorityDot,
        StudyRequestStatusBadge,
        RequestInfo,
        PatientInfo,
        ReportInfo,
        RequestAttachmentsInfo,
        RequestIncidentsInfo,
        TrackingInfo,
        VtBubble
    },
    props: {
        studyRequest: { type: Object },
        casId: { type: Number },
        labId: { type: Number },
        trackingResource: { type: Object },
        tabIndex: { type: Number, default: 0 },
        showCaseReport: { type: Boolean, default: false }
    },
    data() {
        return {
            request: this.studyRequest,
            sessionState: sessionStore.state
        }
    },
    computed: {
        visibleCaseReport() {
            return this.showCaseReport &&
                this.casId &&
                this.request.status.code === REQUEST_STATES.COMPLETED.code &&
                this.hasLabOperationGlobalMixin(this.request.laboratory.labId, CaseOperation.PETITIONER_PRINT)
        }
    },
    watch: {
        async casId() {
            await this._loadRequestByCaseId()
        }
    },
    async mounted() {
        await this._loadRequestByCaseId()

        this.$nextTick(() => {
            if (this.$refs.tabs) {
                this.$refs.tabs.currentTab = this.tabIndex
            }
        })
    },
    methods: {
        async _loadRequestByCaseId() {
            if (this.labId && this.casId) {
                const response = await this.$_toCallUnique(
                    commonRequestService.getRequestByCaseIdForSidePanel(this.labId, this.casId))
                if (response) {
                    this.request = response
                }
            }
        },
        printReport() {
            clinicalRequestPrintService.getRequestReportPdfForPrinting([this.studyRequest.id])
        }
    }
}

</script>

<style lang="scss">

.sr-detail .showtooltip{
  display: block !important;
  cursor: pointer;
}

.sr-detail .noshowtooltip{
  display: none !important;
}

.sr-detail .section-indicator{
  position: absolute;
  width: auto;
  left: 23px;
  margin-top: 5px;
}

.sr-detail .title {
  font-size: font-size(big);
  color: vt-color(primary);
}

.sr-detail .tabs.row {
  height: calc(100vh - 170px) !important;
  border: 1px solid rgba(0, 0, 0, 0.125);
}

.sr-detail .content-scroll {
  height: calc(100vh - 220px);
  overflow-y: auto;
  overflow-x: hidden;
}

.sr-detail .content-scroll.request-info {
  height: calc(100vh - 170px) !important;
}

.sr-detail .content-scroll.report-info,
.sr-detail .content-scroll.tracking-info,
.sr-detail .content-scroll.patient-info {
    height: calc(100vh - 215px) !important;
}

.sr-detail .subrequest-box .vt-tabs .nav-tabs .nav-link.active,
.vt-tabs--small .nav-tabs .nav-link.active {
  border-bottom: 2px solid vt-color(primary);
}

.sr-detail .subrequest-box .vt-tabs .nav-tabs .nav-link,
.vt-tabs--small .nav-tabs .nav-link {
  border-bottom: 2px solid transparent;
}

.sr-detail .subrequest-box .nav-link.active::before {
  background: transparent;
}

@media (max-width: 1300px) {

  .sr-detail .title {
    font-size: font-size(normal);
    color: vt-color(primary);
  }

  .sr-detail .nav-link {
    width: 50px;
  }

  .sr-detail .techniques-tab .nav-link {
    width: auto;
  }

  .sr-detail .content-scroll {
    height: calc(100vh - 200px);
  }

  .sr-detail .tabs.row {
    height: calc(100vh - 160px) !important;
  }
}

</style>
